import { useCallback, useMemo, useState } from 'react';
import { useGetList, useNotify } from 'react-admin';
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AntennaId, SupplyId, SupplyLight } from '@wwf/sdk';
import { getSupplyInventory, updateSupplyInventory } from '@wwf/sdk';
import { SupplyLightCard } from '@app/supply/show/SupplyLightCard';
import {
    SupplyInventoryEditCellDialog,
    type SupplyEditCellState,
    type SupplyInventorySaveValues,
} from '@app/supply/inventory/SupplyInventoryEditCellDialog';

const INVENTORY_QUERY_KEY_PREFIX = ['supply_inventory_per_antenna'] as const;

type AntennaSupplyInventoryProps = {
    antennaId: AntennaId;
    antennaName: string;
};

export const AntennaSupplyInventory = ({
    antennaId,
    antennaName,
}: AntennaSupplyInventoryProps) => {
    const notify = useNotify();
    const queryClient = useQueryClient();

    const { data: supplies, isPending: suppliesPending } =
        useGetList<SupplyLight>('supply', {
            pagination: { page: 0, perPage: 10000 },
            sort: { field: 'name', order: 'ASC' },
            filter: { enabled: true },
        });

    const {
        data: inventoryRows,
        isPending: inventoryPending,
        isError: inventoryError,
    } = useQuery({
        queryKey: [...INVENTORY_QUERY_KEY_PREFIX, antennaId],
        queryFn: () => getSupplyInventory(antennaId),
    });

    const inventoryBySupplyId = useMemo(() => {
        const m = new Map<
            SupplyId,
            {
                count: number;
                inventoryComment: string | null;
                available?: boolean;
            }
        >();
        for (const row of inventoryRows ?? []) {
            m.set(row.supplyId, {
                count: row.inventoryCount ?? 0,
                inventoryComment: row.inventoryComment,
                available: row.available,
            });
        }
        return m;
    }, [inventoryRows]);

    const upsertMutation = useMutation({
        mutationFn: async (payload: {
            supply_id: SupplyId;
            count: number;
            inventoryComment: string | null;
        }) => {
            await updateSupplyInventory(payload.supply_id, antennaId, {
                inventoryCount: payload.count,
                inventoryComment: payload.inventoryComment,
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: INVENTORY_QUERY_KEY_PREFIX,
            });
            notify('Inventory updated', { type: 'success' });
        },
        onError: (err: Error) => {
            notify(err.message ?? 'Update failed', { type: 'error' });
        },
    });

    const [editCell, setEditCell] = useState<SupplyEditCellState | null>(null);

    const openEdit = useCallback(
        (params: Omit<SupplyEditCellState, 'antennaId' | 'antennaName'>) => {
            setEditCell({
                antennaId,
                antennaName,
                ...params,
            });
        },
        [antennaId, antennaName]
    );

    const closeEdit = useCallback(() => {
        setEditCell(null);
    }, []);

    const handleSaveEdit = useCallback(
        (values: SupplyInventorySaveValues) => {
            if (!editCell) return;
            upsertMutation.mutate(
                {
                    supply_id: editCell.supplyId,
                    count: values.count,
                    inventoryComment: values.inventoryComment,
                },
                { onSuccess: closeEdit }
            );
        },
        [closeEdit, editCell, upsertMutation]
    );

    if (suppliesPending || inventoryPending) {
        return (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (inventoryError) {
        return (
            <Typography color="error">
                Could not load inventory. Try again or check your permissions.
            </Typography>
        );
    }

    const supplyList = supplies ?? [];

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Stock count per supply for this antenna. Click count or comment
                to edit.
            </Typography>

            {!supplyList.length ? (
                <Typography variant="body2" color="text.secondary">
                    No enabled supplies to display.
                </Typography>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Supply</TableCell>
                                <TableCell align="center" width={100}>
                                    Count
                                </TableCell>
                                <TableCell>Comment</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {supplyList.map(supply => {
                                const inventory = inventoryBySupplyId.get(
                                    supply.id
                                );
                                const count = inventory?.count ?? 0;
                                const inventoryComment =
                                    inventory?.inventoryComment ?? null;
                                const commentPreview =
                                    inventoryComment &&
                                    inventoryComment.length > 80
                                        ? `${inventoryComment.slice(0, 80)}…`
                                        : inventoryComment;

                                const openEditForRow = () =>
                                    openEdit({
                                        supplyId: supply.id,
                                        supplyName: supply.name ?? 'Supply',
                                        count,
                                        inventoryComment,
                                    });

                                const editableCellSx = {
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' },
                                };

                                return (
                                    <TableRow key={supply.id} hover>
                                        <TableCell>
                                            <SupplyLightCard
                                                record={{
                                                    ...supply,
                                                    available:
                                                        inventory?.available,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            onClick={openEditForRow}
                                            sx={editableCellSx}
                                        >
                                            <Typography variant="body2">
                                                {count}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            onClick={openEditForRow}
                                            sx={editableCellSx}
                                        >
                                            {commentPreview ? (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {commentPreview}
                                                </Typography>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.disabled"
                                                >
                                                    —
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <SupplyInventoryEditCellDialog
                cell={editCell}
                onClose={closeEdit}
                onSave={handleSaveEdit}
                isSavePending={upsertMutation.isPending}
            />
        </Box>
    );
};
